// apps/client/src/hooks/pipelineTracker/useRemSettings.ts
//
// The tenant's settlement bands and broker costs, read and written through the tracker's settings
// callable. Whole-document writes with the loaded version, exactly as the broker roster: a second
// admin saving in between is refused with `aborted` rather than silently overwritten.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { TAXONOMY_CALLABLE } from './reportContract';
import {
  GET_REM_SETTINGS_ACTION,
  SAVE_REM_SETTINGS_ACTION,
  type RemBand,
  type RemBrokerCost,
  type RemSettings,
} from './remContract';
import { invalidateTrackerReports } from './reportCache';

export type RemSaveOutcome = { ok: true } | { ok: false; message: string; conflict?: boolean };

export function useRemSettings(companyId: string | undefined) {
  const [settings, setSettings] = useState<RemSettings | null>(null);
  const [isSeed, setIsSeed] = useState(true);
  const [invalidStored, setInvalidStored] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = await call({ action: GET_REM_SETTINGS_ACTION, companyId });
      const data = res.data as { settings: RemSettings; isSeed: boolean; invalidStoredDocument?: string };
      setSettings(data.settings);
      setIsSeed(data.isSeed);
      setInvalidStored(data.invalidStoredDocument ?? null);
    } catch (err) {
      console.error('[PipelineTracker] getRemSettings failed', err);
      setError('The remuneration settings could not be loaded, so they cannot be edited right now.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async (bands: RemBand[], brokerCosts: Record<string, RemBrokerCost>): Promise<RemSaveOutcome> => {
    if (!companyId || !settings) return { ok: false, message: 'Not ready.' };
    setSaving(true);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = await call({
        action: SAVE_REM_SETTINGS_ACTION,
        companyId,
        bands,
        brokerCosts,
        expectedVersion: settings.version,
      });
      const { version } = res.data as { version: number };
      setSettings({ ...settings, bands, brokerCosts, version });
      setIsSeed(false);
      invalidateTrackerReports();
      return { ok: true };
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code.endsWith('aborted')) {
        console.error('[PipelineTracker] saveRemSettings version conflict', err);
        void load();
        return {
          ok: false,
          conflict: true,
          message: 'Someone else changed these settings while this page was open. Reloaded; check them and try again.',
        };
      }
      console.error('[PipelineTracker] saveRemSettings failed', err);
      return {
        ok: false,
        message: code.endsWith('invalid-argument')
          ? `Not saved: ${(err as Error).message}`
          : 'The settings could not be saved. Nothing was changed.',
      };
    } finally {
      setSaving(false);
    }
  }, [companyId, settings, load]);

  return { settings, isSeed, invalidStored, loading, saving, error, save, reload: load };
}
